/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

/**
 * Keeps the rows of the product comparison table aligned across its two grids.
 *
 * The current product sits in a frozen column and the others in a scrolling
 * grid, so they are two separate CSS grids: a label that wraps in one and not
 * the other would give the same row two heights. This measures every row in
 * both grids and sets the same explicit track heights on each, again whenever
 * a grid resizes or the "more details" rows are revealed.
 *
 * It also drives the sticky product header: shown once the card row has
 * scrolled under the site header and until the table ends, placed over the
 * table's columns, with its scrolling part following the table's horizontal
 * scroll so each product stays above its own column.
 */
class ProductComparisonTable extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {number | null} */
  #frame = null;

  /** Set while applying heights, so the resize the update itself causes is ignored. */
  #applying = false;

  /** @type {number | null} */
  #stickyFrame = null;

  connectedCallback() {
    // First pass right away rather than in a frame callback: frames are not
    // delivered to a background tab, and a page opened there would keep its
    // rows misaligned until the tab is looked at.
    this.#equalize();

    this.#resizeObserver = new ResizeObserver(() => {
      if (!this.#applying) this.#schedule();
    });
    for (const grid of this.#grids()) this.#resizeObserver.observe(grid);

    // Fixed row tracks hide a later reflow of the text, so measure again once
    // the web fonts are in.
    document.fonts?.ready.then(() => this.#schedule());

    // The collapsible rows are toggled by show-more-component through the
    // `hidden` class; recompute once they are visible (and after the reveal
    // transition, when their final size is known).
    this.#mutationObserver = new MutationObserver(() => {
      this.#schedule();
      window.setTimeout(() => this.#schedule(), 350);
    });
    for (const grid of this.#grids()) {
      this.#mutationObserver.observe(grid, { attributes: true, attributeFilter: ['class', 'hidden'] });
    }

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll);
    this.#scroller()?.addEventListener('scroll', this.#onHorizontalScroll, { passive: true });
    this.addEventListener('click', this.#onAddToCart);
    this.#updateSticky();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    if (this.#stickyFrame !== null) cancelAnimationFrame(this.#stickyFrame);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    this.#scroller()?.removeEventListener('scroll', this.#onHorizontalScroll);
    this.removeEventListener('click', this.#onAddToCart);
  }

  /**
   * Clicks the page's main Add to cart for the current product instead of
   * adding a variant of its own. Falls back to the product page when there is
   * no main form on the page.
   * @param {Event} event
   */
  #onAddToCart = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-comparison-add-to-cart]');
    if (!(button instanceof HTMLElement)) return;

    event.preventDefault();

    const forms = [
      ...document.querySelectorAll(`product-form-component[data-product-id="${button.dataset.productId}"]`),
    ].filter((form) => !this.contains(form) && !form.closest('dialog'));
    const mainForm = forms.find((form) => form.closest('.product-details')) ?? forms[0];
    const mainButton = mainForm?.querySelector('add-to-cart-component button');

    if (mainButton instanceof HTMLButtonElement) {
      if (!mainButton.disabled) mainButton.click();
      return;
    }

    if (button.dataset.productUrl) window.location.href = button.dataset.productUrl;
  };

  /** @returns {HTMLElement | null} */
  #scroller() {
    const el = this.querySelector('.product-comparison__scroll');
    return el instanceof HTMLElement ? el : null;
  }

  #onScroll = () => {
    if (this.#stickyFrame !== null) return;
    this.#stickyFrame = requestAnimationFrame(() => {
      this.#stickyFrame = null;
      this.#updateSticky();
    });
  };

  #onHorizontalScroll = () => {
    const track = this.querySelector('[data-sticky-track]');
    const scroller = this.#scroller();
    if (track instanceof HTMLElement && scroller) {
      track.style.transform = `translateX(${-scroller.scrollLeft}px)`;
    }
  };

  /**
   * Shows the sticky header while the card row is above the site header and
   * the table is still on screen, and keeps it over the table's columns.
   */
  #updateSticky() {
    const sticky = this.querySelector('[data-sticky-header]');
    const frozen = this.querySelector('.product-comparison__frozen');
    const ctaCell = this.querySelector('.product-comparison__frozen .product-comparison__button-cell');
    const scroller = this.#scroller();
    if (!(sticky instanceof HTMLElement) || !(frozen instanceof HTMLElement) || !ctaCell || !scroller) return;

    const header = document.getElementById('header-component');
    const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    const table = this.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const frozenRect = frozen.getBoundingClientRect();

    sticky.style.top = `${top}px`;
    sticky.style.left = `${table.left}px`;
    sticky.style.width = `${scrollerRect.right - table.left}px`;
    sticky.style.setProperty('--product-comparison-sticky-column', `${frozenRect.width}px`);

    const visible = ctaCell.getBoundingClientRect().bottom < top && table.bottom > top + sticky.offsetHeight;
    sticky.classList.toggle('is-visible', visible);
    sticky.toggleAttribute('inert', !visible);
    if (visible) this.#onHorizontalScroll();
  }

  /** @returns {HTMLElement[]} */
  #grids() {
    return [...this.querySelectorAll('.product-comparison__grid')].filter((el) => el instanceof HTMLElement);
  }

  /**
   * The frozen grid and the scrolling grid that share the same rows: the
   * always-visible pair, and the "more details" pair when present.
   * @returns {Array<[HTMLElement, HTMLElement]>}
   */
  #pairs() {
    const frozen = this.querySelector('.product-comparison__frozen');
    const scroll = this.querySelector('.product-comparison__scroll');
    if (!frozen || !scroll) return [];

    const pick = (root, extra) =>
      [...root.querySelectorAll('.product-comparison__grid')].find(
        (el) => el instanceof HTMLElement && el.classList.contains('product-comparison__grid--extra') === extra,
      );

    /** @type {Array<[HTMLElement, HTMLElement]>} */
    const pairs = [];
    for (const extra of [false, true]) {
      const a = pick(frozen, extra);
      const b = pick(scroll, extra);
      if (a instanceof HTMLElement && b instanceof HTMLElement) pairs.push([a, b]);
    }
    return pairs;
  }

  #schedule() {
    if (this.#frame !== null) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#equalize();
    });
  }

  #equalize() {
    this.#applying = true;

    for (const [frozenGrid, scrollGrid] of this.#pairs()) {
      if (frozenGrid.classList.contains('hidden') || scrollGrid.classList.contains('hidden')) continue;

      // Remember the Liquid-rendered track list once, and measure against it
      // so previously applied heights never feed back into the next measure.
      for (const grid of [frozenGrid, scrollGrid]) {
        if (!grid.dataset.rowsTemplate) grid.dataset.rowsTemplate = grid.style.gridTemplateRows;
        grid.style.gridTemplateRows = grid.dataset.rowsTemplate;
      }

      const rowCount = frozenGrid.dataset.rowsTemplate.split(/\s+/).filter(Boolean).length;
      if (!rowCount) continue;

      const heights = new Array(rowCount).fill(0);
      const measure = (grid) => {
        [...grid.children].forEach((cell, index) => {
          const row = index % rowCount;
          heights[row] = Math.max(heights[row], cell.getBoundingClientRect().height);
        });
      };
      measure(frozenGrid);
      measure(scrollGrid);

      const tracks = heights.map((height) => `${Math.ceil(height)}px`).join(' ');
      frozenGrid.style.gridTemplateRows = tracks;
      scrollGrid.style.gridTemplateRows = tracks;
    }

    // Let the observers see the applied layout before listening again.
    requestAnimationFrame(() => {
      this.#applying = false;
    });
  }
}

if (!customElements.get('product-comparison-table')) {
  customElements.define('product-comparison-table', ProductComparisonTable);
}
